import { useMemo } from "react";

import {
  addNodeAction,
  availableLanguageCodesSelector,
  primaryContentLanguageCodeSelector,
  type NautilusStore,
} from "@skilitics/nautilus-core-designer-data";

import { createNodeDataCreate } from "../nodes/index.ts";
import { migrateNodeDesignsCreate } from "../nodes/migration/index.ts";
import { getNodeRoleSelector, type NautilusDesignerAppConfig } from "../state/index.ts";
import type { NautilusDesignerAppState } from "../state/shared.ts";
import type { GetManifestByTypeId, GetMigrateFromManifestByTypeId, GetPlaceholderManifest } from "../types/index.ts";

interface Options {
  getManifest: GetManifestByTypeId;
  getPlaceholderManifest: GetPlaceholderManifest;
  getMigrateFromManifest: GetMigrateFromManifestByTypeId;
  rootNodeTypeId: string;
}

const useRunNodeDesignsMigrations = ({ getManifest, getMigrateFromManifest, getPlaceholderManifest }: Options) =>
  useMemo(
    () => (store: NautilusStore<NautilusDesignerAppConfig>) => {
      try {
        const rootNodeParentContext = store.getState().appConfig.rootNodeParentContext;
        const migrate = migrateNodeDesignsCreate({
          getManifest,
          getPlaceholderManifest,
          getMigrateFromManifest,
          rootNodeParentContext,
        });
        const primaryLanguageCode = primaryContentLanguageCodeSelector(store.getState());
        const supportedLanguageCodes = availableLanguageCodesSelector(store.getState());
        const actions = migrate(store.getState().nodeDesigns, {
          primaryLanguageCode,
          supportedLanguageCodes,
        });
        for (const action of actions) {
          store.dispatch(action);
        }
      } catch (err) {
        if (globalThis.location.href.includes("localhost") && err instanceof Error && "message" in err) {
          globalThis.alert(`Error while running migrations: ${err.message}.`);
        }
        console.error("Error while running migrations", err);
        throw err;
      }
    },
    [getManifest, getMigrateFromManifest, getPlaceholderManifest],
  );

const useEnsureRootNode = ({ getManifest, getPlaceholderManifest, rootNodeTypeId }: Options) =>
  useMemo(
    () => (store: NautilusStore<NautilusDesignerAppConfig>) => {
      if (Object.keys(store.getState().nodeDesigns.designsById).length === 0) {
        let addNodeId = 0;
        const getNextId = () => store.getState().nodeDesigns.nextId + addNodeId++;
        const getNodeRole = getNodeRoleSelector(store.getState() as NautilusDesignerAppState);
        const node = createNodeDataCreate({ getManifest, getPlaceholderManifest, getNodeRole, getNextId })(
          rootNodeTypeId,
        );
        store.dispatch(addNodeAction({ node }));
      }
    },
    [getManifest, getPlaceholderManifest, rootNodeTypeId],
  );

export const useNodeDesignsInitializer = (options: Options) => {
  const ensureRootNode = useEnsureRootNode(options);
  const runMigrations = useRunNodeDesignsMigrations(options);

  return useMemo(
    () => (store: NautilusStore<NautilusDesignerAppConfig>) => {
      runMigrations(store);
      ensureRootNode(store);
    },
    [ensureRootNode, runMigrations],
  );
};
